'use client'

import { BuilderBlocks } from '@builder.io/react'

import { mediaCardDefaults } from '@src/components/MediaCard/MediaCard.defaults'
import { VideoPlayer } from '@src/components/VideoPlayer/VideoPlayer'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

import { LocalizedLink } from '../LocalizedLink/LocalizedLink'
import { type MediaCardProps } from './MediaCard.props'

export const MediaCard = (props: MediaCardProps = mediaCardDefaults) => {
  const { isMobile, isTablet } = useBreakpoints()
  const { edit } = getBuilderPreviewMode()
  function getProp<T>(prop: T, mobileProp: T) {
    return isMobile || isTablet ? mobileProp || prop : prop
  }
  const {
    desktopImgSrc,
    mobileImgSrc,
    mediaType,
    brightcoveVideoId,
    videoPlayerAspectRatio,
    playAsPopup,
    desktopHeight,
    mobileHeight,
    redirectUrl,
    showGradient,
    innerPadding,
    builderBlock,
    children,
  } = props

  const Wrapper = redirectUrl ? LocalizedLink : 'div'

  const photoUrl = getProp(desktopImgSrc, mobileImgSrc)
  const photoSize = getProp(desktopHeight, mobileHeight)
  const isMediaVideo = mediaType === 'video'

  if (isMediaVideo && brightcoveVideoId)
    return (
      <div className="relative">
        <VideoPlayer
          videoId={brightcoveVideoId}
          videoPlayerAspectRatio={videoPlayerAspectRatio}
          autoplay={false}
          playAsPopup={playAsPopup}
        />
        <div
          className={cn(
            `absolute inset-0 flex flex-col items-center justify-end rounded-[30px]`,
            { 'pointer-events-none': !edit },
            { 'px-4 py-6': innerPadding === 'small' },
            { 'lg:p-16 px-4 py-8': innerPadding === 'large' },
            { 'bg-[linear-gradient(180deg,rgba(0,0,0,0)_52.41%,#161819_100%)]': showGradient }
          )}
        >
          <BuilderBlocks
            parentElementId={builderBlock?.id}
            dataPath={`component.options.children`}
            blocks={children}
            className="size-full"
          />
        </div>
      </div>
    )

  return (
    <Wrapper
      className={cn(
        `flex flex-col rounded-[30px] bg-cover bg-center bg-no-repeat`,
        { 'px-4 py-6': innerPadding === 'small' },
        { 'lg:p-16 px-4 py-8': innerPadding === 'large' }
      )}
      style={{
        backgroundImage: `${showGradient ? 'linear-gradient(180deg, rgba(0,0,0,0) 52.41%, #000000 100%), ' : ''}url(${photoUrl})`,
        height: photoSize ?? 'auto',
      }}
      href={redirectUrl ?? ''}
    >
      <div className={cn('mt-auto', { 'pointer-events-none': !edit })}>
        <BuilderBlocks
          parentElementId={builderBlock?.id}
          dataPath={`component.options.children`}
          blocks={children}
          className="size-full"
        />
      </div>
    </Wrapper>
  )
}
